import { FC, Fragment, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { StyleProps } from '@summer/jst-react';
import useMedia from 'react-use/lib/useMedia';
import { isMobileQuery, media } from '@higo/ui';
import { useIntl } from 'react-intl';

const Container = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: 0.5rem;
  grid-row-gap: 0.25rem;
  height: fit-content;
  margin-left: 0.5rem;
`;

export const ExaminationOverviewInfoListLabel = styled('div')(
  ({ theme }) => css`
    ${theme.typography.body2}
    color: ${theme.palette.secondary['500']};
    max-width: 8rem;
  `,
);

export const GeneralInformationText = styled('span')(
  ({ theme }) => css`
    ${theme.typography.body2}
    margin: 0.3rem 0 0.3rem 0;
    color: ${theme.palette.secondary['500']};

    ${media.mobile.max} {
      margin-top: 0.6rem;
    }

    & b {
      ${theme.typography.subtitle1}
    }
  `,
);

export const InfoItem = styled('span')(
  ({ theme }) => css`
    display: inline;
    ${theme.typography.body2}

    &:after {
      content: '\\025CF';
      color: ${theme.palette.neutral['700']};
      margin-right: 0.5rem;
      margin-left: 0.5rem;
    }

    &:last-of-type:after {
      content: none;
    }

    &:nth-of-type(2)::after {
      content: '';
      display: block;
    }
  `,
);

export const ExaminationOverviewInfoListValue = styled('div')(
  ({ theme }) => css`
    ${theme.typography.subtitle2}
    color: ${theme.palette.secondary['900']};
  `,
);

export type ExaminationOverviewInfoListProps = {
  items: { translationKey: string; value: ReactNode }[];
} & StyleProps;

export const OverviewInfoList: FC<ExaminationOverviewInfoListProps> = ({
  style,
  className,
  items,
}) => {
  const isMobileView = useMedia(`${isMobileQuery}`);
  const intl = useIntl();

  return !isMobileView ? (
    <Container style={style} className={className}>
      {items.map(({ translationKey, value }) => (
        <Fragment key={translationKey}>
          <ExaminationOverviewInfoListLabel id={`${translationKey}-label`}>
            <FormattedMessage id={translationKey} />:
          </ExaminationOverviewInfoListLabel>
          <ExaminationOverviewInfoListValue id={`${translationKey}-value`}>
            {value}
          </ExaminationOverviewInfoListValue>
        </Fragment>
      ))}
    </Container>
  ) : (
    <Container style={style} className={className}>
      <GeneralInformationText>
        {items.map(({ translationKey, value }) => (
          <Fragment key={translationKey}>
            <InfoItem id={translationKey}>
              {intl.formatMessage({
                id: translationKey,
              })}
              : {value}
            </InfoItem>
          </Fragment>
        ))}
      </GeneralInformationText>
    </Container>
  );
};
